import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RevoltButton } from '../../../components';

function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [attempted, setAttempted] = useState(false);

  const handleVerify = useCallback(async () => {
    const token = searchParams.get('token');

    if (!token) {
      setError('No verification token provided');
      setAttempted(true);
      return;
    }

    setLoading(true);
    setError('');
    setAttempted(true);

    try {
      const response = await fetch('/api/account/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Email verification failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const handleSignInNow = useCallback(async () => {
    try {
      await fetch('/api/account/logout', { method: 'POST', credentials: 'include' });
    } catch (err) {
      // Ignore logout errors; still redirect
    } finally {
      window.location.href = '/auth?view=login';
    }
  }, []);

  if (loading) {
    return (
      <div className="auth-form">
        <div className="auth-header">
          <h1 className="auth-title">Verifying Email</h1>
          <p className="auth-subtitle">Please wait...</p>
        </div>

        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div className="loading-spinner" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
          <p style={{ marginTop: '20px', color: '#999' }}>Verifying your email address...</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
          <RevoltButton variant="outline" onClick={() => navigate('/auth?view=login')}>
            Back to Sign In
          </RevoltButton>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-form">
        <div className="auth-header">
          <h1 className="auth-title">Email Verified!</h1>
          <p className="auth-subtitle">Your account is now active</p>
        </div>

        <div className="alert alert-success">
          <strong>Success!</strong>
          <br />
          Your email has been verified. You can now sign in to your account.
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
          <RevoltButton variant="primary" onClick={handleSignInNow}>
            Sign In Now
          </RevoltButton>
        </div>
      </div>
    );
  }

  if (attempted && error) {
    return (
      <div className="auth-form">
        <div className="auth-header">
          <h1 className="auth-title">Verification Failed</h1>
          <p className="auth-subtitle">This verification link appears to be outdated.</p>
        </div>

        <div className="alert alert-error">
          Please locate the most recent verification email and try again.
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <RevoltButton
            variant="outline"
            onClick={() => navigate('/auth?view=login')}
            style={{ flex: 1 }}
          >
            Back to Sign In
          </RevoltButton>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <div className="auth-header">
        <h1 className="auth-title">Verify Your Email</h1>
        <p className="auth-subtitle">Click below to verify this email address.</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
        <RevoltButton
          variant="primary"
          onClick={handleVerify}
          disabled={loading}
        >
          Verify Email
        </RevoltButton>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
        <RevoltButton variant="outline" onClick={() => navigate('/auth?view=login')}>
          Back to Sign In
        </RevoltButton>
      </div>
    </div>
  );
}

export default VerifyEmail;

